import { cn } from '@core/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef } from 'react';

import ApplyBadge from './svg/apply-badge.svg';
import BuildingBadge from './svg/building-badge.svg';
import StarBadge from './svg/star-badge.svg';

const badgeVariants = cva(
  [
    'rounded-xsmall',
    'px-6 py-2',
    'font-600',
    'inline-flex items-center gap-4',
    'bg-Gray-200 text-Gray-700',
  ],
  {
    variants: {
      dimensions: {
        H24: 'typo-caption1',
        H20: 'typo-caption2',
      },
    },
    defaultVariants: {
      dimensions: 'H24',
    },
  },
);

interface BadgeProps
  extends ComponentPropsWithoutRef<'span'>, VariantProps<typeof badgeVariants> {
  icon?: 'building' | 'star' | 'apply';
  asChild?: boolean;
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, icon, dimensions, children, asChild, ...props }, ref) => {
    const Icon =
      icon === 'building' ? BuildingBadge : icon === 'star' ? StarBadge : null;

    if (icon === 'apply') {
      return <ApplyBadge />;
    }

    const Comp = asChild ? Slot : 'span';

    return (
      <Comp
        ref={ref}
        className={cn(badgeVariants({ dimensions }), className)}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {Icon ? <Icon /> : null}
            {children}
          </>
        )}
      </Comp>
    );
  },
);

Badge.displayName = 'Badge';

export { Badge, type BadgeProps };
